import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { withTranslation } from 'react-i18next';
import IframeResizer from 'iframe-resizer-react';
import Spinner from '../common/CircularSpinner';
import * as notificationActions from "../../redux/actions/notificationsActions";
import * as sugarSessionActions from "../../redux/actions/sugarSessionActions";
import *  as navigationActions from '../../redux/actions/navigationActions';
import * as uiActions from '../../redux/actions/uiActions';
import * as tenantActions from '../../redux/actions/tenantActions';
import TenantAccessDenied from '../tenants/TenantAccessDenied';
import { clearTenantArrivals, withoutForeignTenant } from '../../utils/tenantAccessGuard';
import { writeNewUI } from '../../utils/newUiPreference';
import { writeDeveloperMode } from '../../utils/developerModePreference';
import { writeHideNewButton } from '../../utils/hideNewButtonPreference';

const frameIdPrimary = 'sugarIFrame0';
const frameIdSecondary = 'sugarIFrame1';

export class LegacyIFrame extends React.Component {
    constructor(props) {
        super(props)
        this.ref = React.createRef();
        this.state = {
            isLoading: false,
            error: false,
            frameId: frameIdPrimary,
            path: 'about:blank',
            pathKey: '',
            reportedLocation: '',
            seconds: 10,
        }
    }

    // Routes whose history entries were created by the legacy page itself. Sugar
    // pages that are single page apps (the fireBreak8 measurement reports) push
    // their own entries with pushState and restore the matching view on popstate,
    // and they say so with "iframeHistory" in the navigation message. Back and
    // forward between those routes must therefore only move the address bar here:
    // reloading the frame would throw the page's state away and drop the user
    // back onto the very view they are trying to leave.
    //
    // Reset whenever the frame is actually reloaded - the entries belong to the
    // page instance that created them.
    iframeOwnedPaths = new Set();

    // Unsaved changes on the legacy page (the kanban card editor). The menu
    // replaces the whole iframe, which the page cannot stop by itself, so it
    // reports its state as { unsavedChanges, unsavedMessage }. While that is set,
    // react navigation is held and the page is asked to confirm with its own
    // save / discard dialog ({ confirmLeave }). It acknowledges at once
    // ({ leavePromptShown }) and answers { leaveConfirmed } once the changes are
    // saved or discarded; "continue editing" just drops the held navigation.
    unsavedChanges = false;
    unsavedMessage = '';
    pendingNavigation = null;
    leaveAckTimer = null;

    blockNavigation = (location, action) => {
        if (!this.unsavedChanges) {
            return undefined;
        }
        this.pendingNavigation = { location, action };
        this.sendToFrame({ confirmLeave: true });
        // A page that does not answer (script error, stale cached script) must
        // not trap the user: fall back to a plain confirm.
        clearTimeout(this.leaveAckTimer);
        this.leaveAckTimer = setTimeout(this.confirmLeaveWithoutPage, 1500);
        return false;
    }

    confirmLeaveWithoutPage = () => {
        if (!this.pendingNavigation) {
            return;
        }
        if (window.confirm(this.unsavedMessage || (this.props.t ? this.props.t("UnsavedChangesConfirm") : ''))) {
            this.continueNavigation();
        } else {
            this.pendingNavigation = null;
        }
    }

    continueNavigation = () => {
        const pending = this.pendingNavigation;
        this.pendingNavigation = null;
        this.unsavedChanges = false;
        if (!pending || !this.props.history) {
            return;
        }
        if (pending.action === 'REPLACE') {
            this.props.history.replace(pending.location);
        } else {
            this.props.history.push(pending.location);
        }
    }

    // Plain page loads (window.location.href, closing the tab) bypass history.block.
    onBeforeUnload = (event) => {
        if (!this.unsavedChanges) {
            return undefined;
        }
        event.preventDefault();
        event.returnValue = this.unsavedMessage;
        return this.unsavedMessage;
    }

    sendToFrame = (message) => {
        if (this.ref.current) {
            this.ref.current.sendMessage(message);
        }
    }

    // Returns true when the message was about unsaved changes.
    handleUnsavedMessage = (message) => {
        if ('unsavedChanges' in message) {
            this.unsavedChanges = !!message.unsavedChanges;
            this.unsavedMessage = message.unsavedMessage || '';
        }
        if (message.leavePromptShown) {
            clearTimeout(this.leaveAckTimer);
        }
        if (message.leaveConfirmed) {
            clearTimeout(this.leaveAckTimer);
            this.continueNavigation();
        }
        return 'unsavedChanges' in message || 'leavePromptShown' in message || 'leaveConfirmed' in message;
    }

    resetUnsavedChanges = () => {
        clearTimeout(this.leaveAckTimer);
        this.unsavedChanges = false;
        this.unsavedMessage = '';
        this.pendingNavigation = null;
    }

    searchOfRoute = (route) => {
        const idx = (route || '').indexOf('?');
        return idx === -1 ? '' : route.substring(idx);
    }

    // "new_ui" is a one shot signal: sugar stores the chosen value in the user
    // preferences and reports it back, so it only needs to travel with the single
    // iframe load that follows the toggle. Everything that compares or rewrites the
    // route works on the search string without it.
    stripNewUI = (search) => {
        if (!search || search.indexOf('new_ui') === -1) {
            return search || '';
        }

        const params = new URLSearchParams(search);
        params.delete('new_ui');
        const query = params.toString();
        return query ? '?' + query : '';
    }

    // The route's query without a "tenant=" naming another tenant than the opened one
    // (see withoutForeignTenant): sugar would send the app back to that tenant.
    searchForTenant = () => {
        return withoutForeignTenant(this.props.location.search || '', this.props.tenantId);
    }

    buildPath = (search) => {
        var loc1 = (search || "?");
        if (loc1 !== "?") {
            loc1 = loc1 + "&";
        }
        // Drop any user1id already in the route (first or later parameter); the current
        // one is appended below.
        loc1 = loc1.replace(/([?&])user1id=[^&]*&/g, "$1");

        // The logout route is a plain route, not a private one, so this frame is also
        // rendered after the session has been cleared from the store and there is no
        // user to identify. index.php skips its user1id check for the logout action
        // and whenever the parameter is missing, so leaving it out is what the server
        // expects - reading through a null user here took the logout page down.
        const profile = !!this.props.user ? this.props.user.profile : null;
        if (!profile) {
            // Drop the separator that was appended for the user1id that is not coming.
            const query = loc1 === "?" ? "" : loc1.substring(0, loc1.length - 1);
            return "https://" + this.props.sugarHost + "/index.php" + query;
        }

        loc1 = loc1.replace(profile.sub, "");
        return "https://" + this.props.sugarHost + "/index.php" + loc1 + 'user1id=' + profile.sub;
    }

    // Only "index.php" pages have a matching react route: the route is rebuilt into an
    // index.php url by buildPath(). Standalone sugar pages (mapping3.php and other entry
    // points) have none, so they are reported as null and the caller leaves the address
    // bar as it is instead of writing a mangled path into it.
    //
    // The page url arrives absolute (".../index.php?..."), but the list link href sugar
    // emits in #return-to-list-module is relative ("index.php?..."), so both forms have
    // to be accepted - otherwise the module link in the top bar falls back to the current
    // page and clicking it just reloads it.
    mapYerpUrlToReactRoute = (yerpUrl) => {
        const url = yerpUrl || '';
        let rest;
        const pathIdx = url.indexOf('/index.php');
        if (pathIdx !== -1) {
            rest = url.substring(pathIdx + '/index.php'.length);
        } else if (url.indexOf('index.php') === 0) {
            rest = url.substring('index.php'.length);
        } else {
            return null;
        }

        const locationStr = '/yerp' + rest;
        // The iframe still has "new_ui" in its url right after the toggle, but the react
        // route it is reported back as must not pick it up.
        const searchIdx = locationStr.indexOf('?');
        return searchIdx === -1 ?
            locationStr :
            locationStr.substring(0, searchIdx) + this.stripNewUI(locationStr.substring(searchIdx));
    }

    setNewUI = (newUI) => {
        writeNewUI(newUI);
        this.props.actions.setNewUI(newUI);
    }

    // Sugar only knows the setting on pages rendered through SugarView (which emit
    // FrontEndSugarConfig). Standalone pages such as mapping3.php cannot tell, and
    // leave the value out of the message - then we keep the stored one.
    setNewUIIfReported = (value) => {
        if (value === undefined || value === null) {
            return;
        }
        this.setNewUI(!!value);
    }

    // inDeveloperMode() on the sugar side. Same tri-state contract as the UI setting:
    // pages that do not know it leave the key out and the stored value is kept.
    setDeveloperModeIfReported = (value) => {
        if (value === undefined || value === null) {
            return;
        }
        writeDeveloperMode(!!value);
        this.props.actions.setDeveloperMode(!!value);
    }

    // Sugar hides the create new button for technicians when HIDE_NEW_BUTTON_TECHNICIAN
    // is set. Same tri-state contract: pages that do not know it leave the key out.
    setHideNewButtonIfReported = (value) => {
        if (value === undefined || value === null) {
            return;
        }
        writeHideNewButton(!!value);
        this.props.actions.setHideNewButton(!!value);
    }

    handleOnMessage = (event) => {
        const message = event.message;

        if (!!message && typeof message === 'object' && this.handleUnsavedMessage(message)) {
            return;
        }

        // Maintain a list of logged in sugar instances
        if ("type" in message) {
            if (message.type === "logout_success") {
                this.props.actions.sugarLogoutSuccess(message.sugarHost);
            }
        } else if ("sugarHost" in message) {
            if (!this.props.sugarSession.loggedInInstances.includes(message.sugarHost)) {
                this.props.actions.sugarLoginSuccess(message.sugarHost);
            }
        }

        //handle sugar toasters
        if("sugarToaster" in message){
            var toast = message.sugarToaster;
            switch(toast.iconClass){
                case "toast-success":
                    this.props.actions.notificationSuccess(toast.message);
                    break;
                case "toast-info":
                    this.props.actions.notificationInfo(toast.message);
                    break;
                case "toast-error":
                    this.props.actions.notificationError(toast.message);
                    break;
                case "clear":
                    this.props.actions.clearNotifications();
                    break;
                default:
                    break

            }
        }
        // Sugar reports the effective new/old UI setting (stored in the sugar user
        // preferences) on every page load, also on pages without a navigation message.
        this.setNewUIIfReported(message.newUiSetting);
        this.setDeveloperModeIfReported(message.developerMode);
        this.setHideNewButtonIfReported(message.hideNewButton);

        //const frame = this.ref.current;

        this.setState({
            isLoading: false,
            seconds: 10
        });
        if (!!message.name && (!!message.moduleUrl || !!message.url)) {
            let user_name = '';
            if (!!message.user_name) {
                user_name = message.user_name;
                sessionStorage.setItem("user_name",user_name);
            }

            let sugar_user_id = '';
            if (!!message.sugar_user_id) {
                sugar_user_id = message.sugar_user_id;
                sessionStorage.setItem("sugar_user_id", sugar_user_id);
                // A sugar page loaded for a logged in user: whatever brought the app back
                // to this tenant before, it was not a reload loop.
                this.onTenantAccessConfirmed();
            }

            this.setNewUIIfReported(message.NEW_UI);
            this.setDeveloperModeIfReported(message.developerMode);
            this.setHideNewButtonIfReported(message.hideNewButton);

            const reactRoute = this.mapYerpUrlToReactRoute(message.url);
            const moduleRoute = this.mapYerpUrlToReactRoute(message.moduleUrl || message.url);

            // The page pushed this route into the browser history itself.
            if (!!message.iframeHistory && reactRoute !== null) {
                this.iframeOwnedPaths.add(this.buildPath(this.searchOfRoute(reactRoute)));
            }

            // The top bar link must point at the list ("moduleUrl"), never at the page
            // itself. onIFrameRouteChange() below sets the same thing, but only when the
            // reported route actually changed - a page that reports the same route twice
            // (saving back onto the same DetailView) would otherwise be left with a link
            // to itself.
            //this.props.history.push(message.url),
            this.props.actions.setModule({
                url: moduleRoute || reactRoute || message.url,
                name: message.name
            });

            // No react route for this page -> do not touch the reported location.
            if (reactRoute !== null) {
                this.onIFrameRouteChange(
                    message.name,
                    reactRoute,
                    moduleRoute || reactRoute
                );
            }
        }
        //handle inside iframe spinner

        if ("sugarSpinner" in message) {
            this.setState({
                isLoading: message.sugarSpinner,
                seconds: 10
            });
        }
    }

    initTimer = () => {
        this.myInterval = setInterval(() => {
            this.setState(({ seconds }) => ({
              seconds: seconds > 0 ? seconds - 1 : 0
            }))
          }, 1000)
    }

    healthCheck = () => {
        const transferFailed = () => {
            const message = this.props.t("ErrorLoadingPage");
            //this.props.actions.notificationError(message);
            this.setState({
                isLoading: false,
                error: true,
                //error: false,
            });
        };

        const transferCanceled = () => {
            this.setState({
                isLoading: false,
                error: false,
            });
        };

        const transferSuccess = () => {
            this.setState({
                isLoading: false,
                error: false,
            });
        };


        var oReq = new XMLHttpRequest();
        oReq.open("GET", this.state.path, true);
        oReq.addEventListener("error", transferFailed);
        oReq.addEventListener("abort", transferCanceled);
        oReq.addEventListener("load", transferSuccess);
        oReq.send();
    }

    componentDidMount() {
        if (!!this.props.history && typeof this.props.history.block === 'function') {
            this.unblockHistory = this.props.history.block(this.blockNavigation);
        }
        window.addEventListener('beforeunload', this.onBeforeUnload);

        const path = "https://" + this.props.sugarHost + "/index.php" + (this.searchForTenant() || '?m=2');
        this.initTimer();
        this.setState({
            isLoading: true,
            error: false,
            path: path,
            seconds: 10
        });
    }

    componentDidUpdate(prevProps, prevState) {
        const search = this.searchForTenant();
        const searchWithoutNewUI = this.stripNewUI(search);
        const path = this.buildPath(search);
        // Compare without "new_ui" so that dropping it from the route below is not
        // seen as a route change and does not reload the iframe a second time.
        const pathKey = this.buildPath(searchWithoutNewUI);
        const forceRefresh = this.props.location.state?.clickIndicator !==  prevProps.location.state?.clickIndicator;
        const pathChanged = pathKey !== this.state.pathKey;
        // Back/forward onto a route the legacy page itself put in the history:
        // the page has already restored that view, so only follow along.
        const ownedByIFrame = this.iframeOwnedPaths.has(pathKey);

        if ((pathChanged && !ownedByIFrame) || forceRefresh) {
            this.iframeOwnedPaths.clear();
            // A new page is loaded; the old one's state goes with it.
            this.resetUnsavedChanges();
            this.setState({
                isLoading: true,
                error: false,
                path: path,
                pathKey: pathKey,
                seconds: 10,
                frameId: this.getNewFrameId(),
            });
        } else if (pathChanged) {
            this.setState({ pathKey: pathKey });
        }

        // The iframe has now been pointed at a url carrying "new_ui", so take it out of
        // the route again. Otherwise it would stay in the address bar and be resent on
        // every later reload, refresh or bookmarked visit.
        if (search !== searchWithoutNewUI && !!this.props.history) {
            this.props.history.replace({
                pathname: this.props.location.pathname,
                search: searchWithoutNewUI,
                state: this.props.location.state,
            });
        }

        if (this.state.isLoading && this.state.seconds === 0 && prevState.seconds > 0) {
            this.healthCheck();
        }
    }

    componentWillUnmount() {
        if (this.unblockHistory) {
            this.unblockHistory();
        }
        window.removeEventListener('beforeunload', this.onBeforeUnload);
        clearTimeout(this.leaveAckTimer);
    }

    // The spinner is cleared by a message from the iframe (sugarSpinner:false or the
    // navigation message a loaded page sends). A page that never posts one - a download,
    // a popup that takes over the frame, an error page - would leave it up for good, so
    // clicking it takes it away. Nothing is cancelled: the frame keeps loading, the
    // spinner just stops covering it.
    dismissSpinner = () => {
        if (this.state.isLoading) {
            this.setState({ isLoading: false });
        }
    }

    onTenantAccessConfirmed = () => {
        const tenantId = this.props.tenantId;
        if (!tenantId) {
            return;
        }
        clearTenantArrivals(tenantId);
        if (this.props.deniedTenantId === tenantId && !!this.props.actions.tenantAccessConfirmed) {
            this.props.actions.tenantAccessConfirmed(tenantId);
        }
    }

    // The signed in account has no access to the tenant (utils/tenantAccessGuard.js).
    // The frame must not be loaded then: sugar would answer with its login page, which
    // reloads the whole app straight back here.
    isTenantAccessDenied = () => {
        return !!this.props.tenantId && this.props.deniedTenantId === this.props.tenantId;
    }

    getNewFrameId = () => {
        return this.state.frameId === frameIdPrimary ? frameIdSecondary : frameIdPrimary;
    }

    onIFrameRouteChange = (title, location, moduleUrl) => {
        if (location !== this.state.reportedLocation) {
            this.setState({
                reportedLocation: location
            });

            // Optional: not every host of the iframe cares about the reported route
            // (the logout iframe does not).
            if (typeof this.props.onIFrameRouteChange === 'function') {
                this.props.onIFrameRouteChange(title, location, moduleUrl);
            }
        }
    }

    render() {
        if (this.isTenantAccessDenied()) {
            return <TenantAccessDenied tenantId={this.props.tenantId} />;
        }

        return (
            <div style={{margin: "0 -1em", height: this.props.contentHeight}}>
                {!!this.props.sugarHost && (
                    <IframeResizer
                        allow="geolocation; microphone; camera; clipboard-write"
                        key={this.state.frameId}
                        id={this.state.frameId}
                        src={this.state.path}
                        style={{width: "100%", height: this.props.contentHeight}}
                        sizeHeight={false}
                        scrolling={true}
                        heightCalculationMethod="lowestElement"
                        tolerance="90"
                        checkOrigin={false}
                        frameBorder="0"
                        warningTimeout={10000}
                        onMessage={this.handleOnMessage}
                        forwardRef={this.ref}
                    />
                )}
                <Spinner open={this.state.isLoading} onClose={this.dismissSpinner} />
            </div>
        );
    }
}


function mapStateToProps(state) {
    return {
        sugarSession: state.sugarSession,
        user: state.oidc.user,
        contentHeight: state.ui.contentHeight,
        resourceAccess: state.resourceAccess,
        tenantId: !!state.tenant ? state.tenant.id : null,
        deniedTenantId: !!state.tenantAccess ? state.tenantAccess.deniedTenantId : null
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: {
            sugarLoginSuccess: bindActionCreators(sugarSessionActions.sugarLoginSuccess, dispatch),
            sugarLogoutSuccess: bindActionCreators(sugarSessionActions.sugarLogoutSuccess, dispatch),
            notificationError: bindActionCreators(notificationActions.notificationError, dispatch),
            notificationInfo: bindActionCreators(notificationActions.notificationInfo, dispatch),
            notificationSuccess: bindActionCreators(notificationActions.notificationSuccess, dispatch),
            notificationWarning: bindActionCreators(notificationActions.notificationWarning, dispatch),
            clearNotifications: bindActionCreators(notificationActions.clearNotifications, dispatch),
            setModule: bindActionCreators(navigationActions.setModule, dispatch),
            setNewUI: bindActionCreators(uiActions.setNewUI, dispatch),
            setDeveloperMode: bindActionCreators(uiActions.setDeveloperMode, dispatch),
            setHideNewButton: bindActionCreators(uiActions.setHideNewButton, dispatch),
            tenantAccessConfirmed: bindActionCreators(tenantActions.tenantAccessConfirmed, dispatch)
        }
    };
}

export default compose(withTranslation('common'),
    connect(
        mapStateToProps,
        mapDispatchToProps
    ))(LegacyIFrame);
